/**
 * Store principal pour la configuration du véhicule
 * Gère l'état global de la configuration : grade sélectionné, items, couleurs
 */

import { defineStore } from 'pinia';
import type {
  GradeItem,
  ConfigItem
} from '../types';
import { dataService } from '../services/DataService';
import { PricingService, type PricingInputs } from '../services/PricingService';
import { useMaterialStore } from './materialStore';

/**
 * Helper pour récupérer le prix d'une teinte depuis les items
 */
function getTeintePriceFromItems(teinteId: string | null, categoryId: string): number {
  if (!teinteId) return 0;

  // Extraire le code couleur du path (ex: BC_WB3 de EXT-GRD_P10GL-BG_A-BC_WB3)
  const colorCodeMatch = teinteId.match(/(BC_|INTBASE_)[A-Z0-9_]+/);
  const colorCode = colorCodeMatch ? colorCodeMatch[0] : null;

  if (!colorCode) return 0;

  try {
    const itemsConfig = dataService.getItems();
    
    // Chercher l'item correspondant dans la catégorie
    for (const category of itemsConfig.ITEMS) {
      if (category.category_id === categoryId) {
        for (const group of category.groups) {
          const item = group.items.find(i => i.item_id === colorCode);
          if (item && typeof item.price === 'number') {
            return item.price;
          }
        }
      }
    }
  } catch (error) {
    console.warn('Erreur lors de la récupération du prix de la teinte:', error);
  }

  return 0;
}

/**
 * État du store de configuration
 */
interface ConfigState {
  /** Grade actuellement sélectionné */
  selectedGrade: GradeItem | null;
  /** Items sélectionnés (clé: item_id, valeur: true) */
  selectedItems: Record<string, boolean>;
  /** Couleur extérieure sélectionnée */
  selectedExteriorColor: string | null;
  /** Couleur intérieure sélectionnée */
  selectedInteriorColor: string | null;
  /** État de chargement */
  isLoading: boolean;
  /** Erreur actuelle */
  error: string | null;
}

type QueryConfigPayload = {
  grade?: string;
  gradeId?: string;
  grade_id?: string;
  selectedItems?: string[];
  exteriorColor?: string;
  interiorColor?: string;
  [key: string]: unknown;
};

const decodeQueryConfigValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    console.warn('Décodage de la configuration depuis la query a échoué :', error);
    return value;
  }
};

const decodeBase64ConfigValue = (value: string): string => {
  if (typeof globalThis.atob === 'function') {
    return globalThis.atob(value);
  }

  throw new Error('Aucune méthode de décodage Base64 disponible');
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string');

const isQueryConfigPayload = (value: unknown): value is QueryConfigPayload => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const payload = value as QueryConfigPayload;
  const hasGradeIdentifier =
    typeof payload.grade === 'string' ||
    typeof payload.gradeId === 'string' ||
    typeof payload.grade_id === 'string';
  const selectedItemsValid =
    payload.selectedItems === undefined || isStringArray(payload.selectedItems);
  const exteriorColorValid =
    payload.exteriorColor === undefined || typeof payload.exteriorColor === 'string';
  const interiorColorValid =
    payload.interiorColor === undefined || typeof payload.interiorColor === 'string';

  return hasGradeIdentifier && selectedItemsValid && exteriorColorValid && interiorColorValid;
};

/**
 * Store de configuration du véhicule
 */
export const useConfigStore = defineStore('config', {
  state: (): ConfigState => ({
    selectedGrade: null,
    selectedItems: {},
    selectedExteriorColor: null,
    selectedInteriorColor: null,
    isLoading: false,
    error: null,
  }),

  getters: {
    /**
     * Retourne le grade actuellement sélectionné
     */
    currentGrade: (state): GradeItem | null => state.selectedGrade,

    /**
     * Retourne les items disponibles pour le grade actuel
     */
    availableItems(state): ConfigItem[] {
      if (!state.selectedGrade) return [];

      try {
        const itemsConfig = dataService.getItems();
        const allItems: ConfigItem[] = [];

        // Aplatir toutes les catégories et groupes
        for (const category of itemsConfig.ITEMS) {
          for (const group of category.groups) {
            allItems.push(...group.items);
          }
        }

        return allItems;
      } catch (error) {
        console.error('Erreur lors de la récupération des items:', error);
        return [];
      }
    },

    /**
     * Retourne un résumé de la configuration complète
     */
    selectedConfiguration: (state) => ({
      grade: state.selectedGrade,
      selectedItems: Object.keys(state.selectedItems).filter(id => state.selectedItems[id]),
      exteriorColor: state.selectedExteriorColor,
      interiorColor: state.selectedInteriorColor
    }),

    /**
     * Getter utilisé par la garde du router pour valider l'accès à la simulation
     */
    isConfigValid: (state): boolean => !!state.selectedGrade && !!state.selectedExteriorColor,

    /**
     * Prix total via PricingService (inclut le prix des teintures)
     */
    totalPrice(state): number {
      // Calculer les items disponibles
      let availableItems: ConfigItem[] = [];
      if (state.selectedGrade) {
        try {
          const itemsConfig = dataService.getItems();
          for (const category of itemsConfig.ITEMS) {
            for (const group of category.groups) {
              availableItems.push(...group.items);
            }
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des items:', error);
        }
      }

      // Calculer les prix des teintures
      const exteriorTeintePrice = getTeintePriceFromItems(state.selectedExteriorColor, 'BODYCOLOR');
      const interiorTeintePrice = getTeintePriceFromItems(state.selectedInteriorColor, 'INT_BASE');

      return PricingService.calculateTotalPrice({
        grade: state.selectedGrade,
        availableItems,
        selectedItems: state.selectedItems,
        exteriorTeintePrice,
        interiorTeintePrice
      } as PricingInputs);
    },

    /**
     * Prix total formaté (inclut le prix des teintures, EUR fixe)
     */
    formattedTotalPrice(state): string {
      // Calculer les items disponibles
      let availableItems: ConfigItem[] = [];
      if (state.selectedGrade) {
        try {
          const itemsConfig = dataService.getItems();
          for (const category of itemsConfig.ITEMS) {
            for (const group of category.groups) {
              availableItems.push(...group.items);
            }
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des items:', error);
        }
      }

      // Calculer les prix des teintures
      const exteriorTeintePrice = getTeintePriceFromItems(state.selectedExteriorColor, 'BODYCOLOR');
      const interiorTeintePrice = getTeintePriceFromItems(state.selectedInteriorColor, 'INT_BASE');

      const total = PricingService.calculateTotalPrice({
        grade: state.selectedGrade,
        availableItems,
        selectedItems: state.selectedItems,
        exteriorTeintePrice,
        interiorTeintePrice
      } as PricingInputs);
      return total.toLocaleString('fr-FR') + ' €';
    }
  },

  actions: {
    /**
     * Charge la liste des grades disponibles
     */
    async loadGrades(): Promise<void> {
      this.isLoading = true;
      this.error = null;

      try {
        // Les grades sont chargés via le gradeStore
        // Cette action pourrait être déplacée vers gradeStore si nécessaire
        await dataService.getAvailableGrades();
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Erreur lors du chargement des grades';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Sélectionne un grade et charge ses données
     * @param gradeId ID du grade à sélectionner
     */
    async selectGrade(gradeId: string): Promise<void> {
      this.isLoading = true;
      this.error = null;

      try {
        await dataService.setCurrentGrade(gradeId);
        this.selectedGrade = dataService.getCurrentGrade();

        // Conserver la couleur extérieure actuellement sélectionnée pour la vérifier dans le nouveau grade
        const previousExteriorColor = this.selectedExteriorColor;

        // Charger les matériaux en passant la couleur extérieure précédente
        // fetchMaterials() conservera la couleur si elle est disponible, sinon sélectionnera BC_ZHJ
        const materialStore = useMaterialStore();
        await materialStore.fetchMaterials(gradeId, previousExteriorColor || undefined);

        // Synchroniser les selections depuis materialStore
        this.selectedExteriorColor = materialStore.selectedExteriorColor || null;
        this.selectedInteriorColor = materialStore.selectedInteriorColor || null;

        // Réinitialiser les items
        this.selectedItems = {};

      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Erreur lors de la sélection du grade';
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Sélectionne ou désélectionne un item/option
     * @param itemId ID de l'item à sélectionner
     */
    selectItem(itemId: string): void {
      if (this.selectedItems[itemId]) {
        // Désélectionner
        this.selectedItems[itemId] = false;
      } else {
        // Sélectionner
        this.selectedItems[itemId] = true;
      }
    },

    /**
     * Change la couleur extérieure
     * @param colorId ID de la couleur extérieure
     */
    selectExteriorColor(colorId: string): void {
      this.selectedExteriorColor = colorId;
    },

    /**
     * Change la couleur intérieure
     * @param colorId ID de la couleur intérieure
     */
    selectInteriorColor(colorId: string): void {
      this.selectedInteriorColor = colorId;
    },

    /**
     * Réinitialise complètement la configuration
     */
    resetConfig(): void {
      this.selectedGrade = null;
      this.selectedItems = {};
      this.selectedExteriorColor = null;
      this.selectedInteriorColor = null;
      this.error = null;

      // Réinitialiser le DataService
      dataService.reset();
    },
    /**
     * Charge la configuration encodée depuis la query string Base64
     * @param config chaîne encodée à parser
     */
    loadFromQuery(config: string): void {

      const trimmedConfig = config.trim();
      if (!trimmedConfig) {
        return;
      }

      const decodedConfig = decodeQueryConfigValue(trimmedConfig);
      let base64Payload: string;

      try {
        base64Payload = decodeBase64ConfigValue(decodedConfig);
      } catch (error) {
        console.error('Erreur lors du décodage Base64 de la configuration :', error);
        return;
      }

      try {
        const parsedPayload: unknown = JSON.parse(base64Payload);
        if (!isQueryConfigPayload(parsedPayload)) {
          console.warn('Configuration depuis la query invalide :', parsedPayload);
          return;
        }

      } catch (error) {
        console.error('Erreur lors du parsing JSON de la configuration depuis la query :', error);
      }
    }

  }
});
